import { useLingui } from '@lingui/react/macro';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { useTranslation } from '@/lib/i18n';
import type { PartnerNgoProject } from './use-partner-ngos';

/** One "1700 / Beneficiarios directos" pair under the cover. */
function Reach(props: { value: number; label: string }) {
	const { language } = useTranslation();
	return (
		<div className="flex flex-col">
			<span className="text-2xl leading-[1.5] font-medium text-black tabular-nums">
				{formatNumber(language, props.value, 0)}
			</span>
			<span className="text-xs leading-normal text-[#7a7a7a]">{props.label}</span>
		</div>
	);
}

/**
 * A project as the partner-NGO modal draws it (`216:6395`): a 676x313 card split down the middle —
 * cover and reach figures on the left, funding progress over the title and summary on the right.
 *
 * A different card from the homepage carousel's, not a variant of it: that one is a narrow portrait
 * tile with the NGO lockup, this one is a wide landscape panel carrying beneficiary counts and a
 * description the carousel never shows. Keeping them apart is what lets each match its own hand-off.
 *
 * Deliberately not a link. There is no public project page to point it at — the nearest real
 * destination is the campaign on a merchant portal, which is a different page than the project — so a
 * "Ver más" here would go nowhere. Same call the homepage card already makes.
 */
export function NgoProjectCard({ project }: { project: PartnerNgoProject }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const raised = Math.round(project.collectedCents / 100);
	const goal = project.economicGoal;
	const pct = goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : undefined;
	const hasReach = project.beneficiaries.direct > 0 || project.beneficiaries.indirect > 0;

	return (
		<article className="flex flex-col gap-4 rounded-3xl bg-white p-4 shadow-[0_9px_24px_0_rgba(46,20,0,0.12)] sm:flex-row">
			<div className="flex shrink-0 flex-col gap-4 sm:w-[292px]">
				<div className="relative h-[197px] overflow-hidden rounded-lg">
					<Image
						src={project.coverImage}
						alt={project.name}
						height={394}
						aspectRatio={292 / 197}
						objectFit="cover"
						className="absolute inset-0 size-full object-cover!"
					/>
					{/* The hand-off darkens the top-left corner so a pale photograph still separates from the
					    white card behind it. */}
					<div aria-hidden className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent" />
				</div>

				{/* Hidden when the project records no reach at all. A project that helps nobody does not
				    exist — a pair of zeroes means the NGO never filled the field in, and printing "0
				    beneficiarios" states something false rather than admitting we do not know. */}
				{hasReach && (
					<div className="flex gap-10">
						<Reach value={project.beneficiaries.direct} label={t`Direct beneficiaries`} />
						<Reach value={project.beneficiaries.indirect} label={t`Indirect beneficiaries`} />
					</div>
				)}
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-4">
				<div className="flex flex-col gap-[5px]">
					{/* Whole euros, as the hand-off prints them ("1.860 € de 15.000 €"). */}
					<p className="text-xs leading-[1.3] font-medium tracking-[-0.02em]">
						<span className="text-ink">{formatNumber(language, raised, 0)} €</span>
						{goal ? (
							<span className="text-ink/50">
								{' '}
								{t`of`} {formatNumber(language, goal, 0)} €
							</span>
						) : null}
					</p>
					{pct !== undefined && (
						<div className="bg-sand h-[3px] overflow-hidden rounded">
							<div className="bg-brand h-full rounded-full" style={{ width: `${pct}%` }} />
						</div>
					)}
				</div>

				<h3 className="text-ink text-xl leading-[1.4] font-medium tracking-[-0.02em]">{project.name}</h3>

				{/* Six lines is the height the hand-off gives this column; the server already caps the text,
				    so the clamp is what keeps a short cap and a long one the same card height. */}
				<p className="line-clamp-6 text-sm leading-[22px] text-black">{project.description}</p>
			</div>
		</article>
	);
}
